/**
 * EmptyState Component
 *
 * Displays a consistent empty state with icon, title, description, and optional CTA.
 * Use for empty lists, no search results, and "no data" scenarios.
 *
 * @module components/common/EmptyState
 */

import { memo } from 'react';
import { Box, Text, Button } from '@mantine/core';
import { IconFolderOff } from '@tabler/icons-react';
import type { EmptyStateProps, EmptyStateSize } from './EmptyState.types';
import styles from './EmptyState.module.css';

/** Icon sizes by component size */
const ICON_SIZES: Record<EmptyStateSize, number> = {
  sm: 32,
  md: 48,
  lg: 64,
};

/**
 * EmptyState Component
 *
 * Displays a consistent empty state with icon, title, description, and optional CTA.
 * Use for empty lists, no search results, and "no data" scenarios.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <EmptyState title="No tasks found" />
 *
 * // With description and action
 * <EmptyState
 *   title="No geometries"
 *   description="Add a marker or shape to get started"
 *   action={{
 *     label: "Add Geometry",
 *     onClick: handleAddGeometry
 *   }}
 * />
 *
 * // With custom icon and size
 * <EmptyState
 *   title="No results"
 *   description="Try adjusting your search filters"
 *   icon={<IconSearch size={48} />}
 *   size="lg"
 * />
 * ```
 */
export const EmptyState = memo(function EmptyState({
  title,
  description,
  hint,
  icon,
  action,
  size = 'md',
  className,
}: EmptyStateProps) {
  const iconSize = ICON_SIZES[size];

  // Build container class with size variant
  const sizeClass = styles[`size${size.charAt(0).toUpperCase()}${size.slice(1)}` as keyof typeof styles];
  const containerClass = [styles.container, sizeClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <Box className={containerClass}>
      <div className={styles.iconWrapper}>
        {icon || <IconFolderOff size={iconSize} className={styles.icon} />}
      </div>

      <Text className={styles.title}>{title}</Text>

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      {hint && (
        <Text className={styles.hint}>{hint}</Text>
      )}

      {action && (
        <Button
          variant={action.variant || 'filled'}
          onClick={action.onClick}
          className={styles.actionButton}
        >
          {action.label}
        </Button>
      )}
    </Box>
  );
});

EmptyState.displayName = 'EmptyState';
